import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
    PiAirplaneTiltDuotone,
    PiArrowRightBold,
    PiBellSimpleRingingDuotone,
    PiBookOpenTextDuotone,
    PiCalendarCheckDuotone,
    PiChatCircleTextDuotone,
    PiCurrencyDollarDuotone,
    PiFileDuotone,
    PiGearDuotone,
    PiInfoDuotone,
    PiMapPinLineDuotone,
    PiSirenDuotone,
    PiUsersDuotone,
    PiWarningDuotone,
    PiWarehouseDuotone,
} from 'react-icons/pi';
import api from '@v2/lib/api';
import { isSuperAdmin, isVendedor } from '@v2/lib/auth';
import { Skeleton } from '@v2/design-system';
import type { Announcement, CompanyDocument, CompanyEvent } from '@v2/types/intranet';

function annGradient(type: Announcement['type']) {
    switch (type) {
        case 'alert':   return 'from-red-600 to-rose-800';
        case 'warning': return 'from-amber-500 to-orange-700';
        case 'info':    return 'from-blue-500 to-indigo-700';
        default:        return 'from-sky-600 to-sky-800';
    }
}

function annIcon(type: Announcement['type']) {
    switch (type) {
        case 'alert':   return PiSirenDuotone;
        case 'warning': return PiWarningDuotone;
        case 'info':    return PiInfoDuotone;
        default:        return PiBellSimpleRingingDuotone;
    }
}

function catGradient(cat: string) {
    switch (cat) {
        case 'governance': return 'from-slate-600 to-slate-800';
        case 'hr':         return 'from-violet-600 to-violet-800';
        case 'procedures': return 'from-teal-600 to-teal-800';
        case 'compliance': return 'from-orange-600 to-orange-800';
        default:           return 'from-gray-500 to-gray-700';
    }
}

function evtBadge(type: CompanyEvent['type']) {
    switch (type) {
        case 'holiday':  return 'bg-red-100 text-red-700';
        case 'birthday': return 'bg-purple-100 text-purple-700';
        case 'meeting':  return 'bg-blue-100 text-blue-700';
        case 'training': return 'bg-amber-100 text-amber-700';
        default:         return 'bg-orange-100 text-orange-700';
    }
}

function useAnnouncements() {
    return useQuery<Announcement[]>({
        queryKey: ['intranet', 'announcements'],
        queryFn: () => api.get('/intranet/announcements').then((r) => r.data.data),
        staleTime: 5 * 60 * 1000,
    });
}

function useEvents() {
    return useQuery<CompanyEvent[]>({
        queryKey: ['intranet', 'events', 'upcoming'],
        queryFn: () =>
            api.get('/intranet/events', { params: { from: new Date().toISOString().slice(0, 10) } })
               .then((r) => r.data.data),
        staleTime: 10 * 60 * 1000,
    });
}

function useDocuments() {
    return useQuery<CompanyDocument[]>({
        queryKey: ['intranet', 'documents'],
        queryFn: () => api.get('/intranet/documents').then((r) => r.data.data),
        staleTime: 10 * 60 * 1000,
    });
}

function SectionHeader({ title, href }: { title: string; href?: string }) {
    return (
        <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            {href && (
                <Link to={href} className="flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                    Ver tudo <PiArrowRightBold className="h-3 w-3" />
                </Link>
            )}
        </div>
    );
}

function Hero({ pinned, totalAnn, totalEvt, totalDoc }: { pinned: Announcement | undefined; totalAnn: number; totalEvt: number; totalDoc: number }) {
    const today   = new Date();
    const dayName = today.toLocaleDateString('pt-BR', { weekday: 'long' });
    const dayNum  = today.getDate();
    const month   = today.toLocaleDateString('pt-BR', { month: 'long' });
    const year    = today.getFullYear();

    return (
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-zinc-950 via-zinc-900 to-slate-900 px-8 py-9 text-white shadow-xl">
            <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-primary/20 blur-3xl" />
            <div className="pointer-events-none absolute bottom-0 left-1/3 h-40 w-40 rounded-full bg-sky-500/10 blur-2xl" />

            <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                <div className="max-w-xl space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-widest text-zinc-400">Portal Buckler</p>
                    {pinned ? (
                        <>
                            <h1 className="text-2xl font-bold leading-snug lg:text-3xl">{pinned.title}</h1>
                            {pinned.content && (
                                <p className="text-sm text-zinc-300 line-clamp-2">{pinned.content}</p>
                            )}
                        </>
                    ) : (
                        <h1 className="text-2xl font-bold lg:text-3xl">Bem-vindo ao portal interno</h1>
                    )}
                    <Link
                        to="/company/announcements"
                        className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2 text-sm font-medium text-white transition hover:bg-white/20"
                    >
                        Ver avisos <PiArrowRightBold className="h-3.5 w-3.5" />
                    </Link>
                </div>

                <div className="flex shrink-0 flex-col items-end gap-5">
                    <div className="text-right">
                        <p className="text-xs capitalize text-zinc-400">{dayName}</p>
                        <p className="text-4xl font-bold tabular-nums leading-none">{dayNum}</p>
                        <p className="text-sm capitalize text-zinc-300">{month}, {year}</p>
                    </div>
                    <div className="flex gap-6 border-t border-white/10 pt-4">
                        {([['Avisos', totalAnn], ['Eventos', totalEvt], ['Docs', totalDoc]] as [string, number][]).map(([label, val]) => (
                            <div key={label} className="text-center">
                                <p className="text-xl font-bold tabular-nums">{val}</p>
                                <p className="text-xs text-zinc-400">{label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function AnnCard({ a }: { a: Announcement }) {
    const Icon = annIcon(a.type);
    const grad = annGradient(a.type);
    return (
        <div className="w-52 shrink-0 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
            <div className={`flex h-28 items-center justify-center bg-gradient-to-br ${grad}`}>
                <Icon className="h-10 w-10 text-white/80" />
            </div>
            <div className="p-3.5">
                <span className="mb-1.5 inline-block rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600 dark:bg-zinc-800 dark:text-zinc-400">
                    {a.type_label}
                </span>
                <p className="text-sm font-semibold leading-snug text-gray-900 line-clamp-2 dark:text-gray-100">
                    {a.title}
                </p>
                <p className="mt-2 text-[11px] text-gray-400">
                    {new Date(a.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
                </p>
            </div>
        </div>
    );
}

function DocCard({ d }: { d: CompanyDocument }) {
    const grad = catGradient(d.category);
    return (
        <Link
            to={`/company/documents/${d.id}`}
            className="group overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm transition hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900"
        >
            <div className={`flex h-20 items-center justify-center bg-gradient-to-br ${grad}`}>
                <PiFileDuotone className="h-8 w-8 text-white/80" />
            </div>
            <div className="p-3.5">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-zinc-500">
                    {d.category_label}
                </p>
                <p className="text-sm font-semibold text-gray-900 line-clamp-2 group-hover:text-primary dark:text-gray-100">
                    {d.title}
                </p>
            </div>
        </Link>
    );
}

function EventRow({ e }: { e: CompanyEvent }) {
    const d     = new Date(e.event_date + 'T00:00:00');
    const day   = d.getDate();
    const month = d.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
    return (
        <div className="flex items-start gap-3 py-3">
            <div className="flex w-10 shrink-0 flex-col items-center rounded-lg border border-gray-100 bg-gray-50 py-1.5 text-center dark:border-zinc-800 dark:bg-zinc-800">
                <span className="text-[10px] uppercase leading-none text-gray-400 dark:text-zinc-500">{month}</span>
                <span className="text-lg font-bold leading-tight text-gray-900 dark:text-gray-100">{day}</span>
            </div>
            <div className="min-w-0 flex-1 pt-0.5">
                <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium leading-snug text-gray-900 line-clamp-2 dark:text-gray-100">{e.title}</p>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${evtBadge(e.type)}`}>{e.type_label}</span>
                </div>
                {e.description && <p className="mt-0.5 text-xs text-gray-400 line-clamp-1">{e.description}</p>}
            </div>
        </div>
    );
}

const MODULES = [
    { to: '/comex',         icon: PiAirplaneTiltDuotone,      label: 'COMEX',      color: 'bg-sky-50 text-sky-600' },
    { to: '/shipments',     icon: PiMapPinLineDuotone,         label: 'Embarques',  color: 'bg-indigo-50 text-indigo-600' },
    { to: '/estoque',       icon: PiWarehouseDuotone,          label: 'Estoque CD', color: 'bg-teal-50 text-teal-600' },
    { to: '/clients',       icon: PiUsersDuotone,              label: 'Clientes',   color: 'bg-violet-50 text-violet-600' },
    { to: '/commissioning', icon: PiCurrencyDollarDuotone,     label: 'Comissão',   color: 'bg-emerald-50 text-emerald-600' },
    { to: '/products',      icon: PiBookOpenTextDuotone,       label: 'Produtos',   color: 'bg-amber-50 text-amber-600' },
    { to: '/ploomes-deals', icon: PiChatCircleTextDuotone,     label: 'Ploomes',    color: 'bg-rose-50 text-rose-600' },
] as const;

export default function HomePage() {
    const { data: announcements = [], isLoading: loadingAnn } = useAnnouncements();
    const { data: events = [],        isLoading: loadingEvt } = useEvents();
    const { data: documents = [],     isLoading: loadingDoc } = useDocuments();

    const pinned  = announcements.find((a) => a.pinned);
    const regular = announcements.filter((a) => !a.pinned);
    const hideModulesForSeller = isVendedor();

    return (
        <div className="space-y-7">
            <Hero
                pinned={pinned}
                totalAnn={announcements.length}
                totalEvt={events.length}
                totalDoc={documents.length}
            />

            {/* Avisos — horizontal scroll */}
            <section>
                <SectionHeader title="Avisos" href="/company/announcements" />
                {loadingAnn ? (
                    <div className="flex gap-4">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="w-52 shrink-0 space-y-3">
                                <Skeleton className="h-28 w-full rounded-xl" />
                                <Skeleton className="h-3 w-16" />
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-3 w-24" />
                            </div>
                        ))}
                    </div>
                ) : announcements.length === 0 ? (
                    <p className="text-sm text-gray-400">Nenhum aviso publicado.</p>
                ) : (
                    <div className="flex gap-4 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        {[...(pinned ? [pinned] : []), ...regular].map((a) => <AnnCard key={a.id} a={a} />)}
                    </div>
                )}
            </section>

            {/* Main 2-col */}
            <div className="grid gap-7 xl:grid-cols-3">
                <div className="space-y-7 xl:col-span-2">
                    {/* Documents */}
                    <section>
                        <SectionHeader title="Documentos" href="/company/documents" />
                        {loadingDoc ? (
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                                {[1, 2, 3, 4].map((i) => (
                                    <div key={i} className="space-y-2">
                                        <Skeleton className="h-20 w-full rounded-xl" />
                                        <Skeleton className="h-3 w-1/3" />
                                        <Skeleton className="h-4 w-full" />
                                    </div>
                                ))}
                            </div>
                        ) : documents.length === 0 ? (
                            <p className="text-sm text-gray-400">Nenhum documento publicado.</p>
                        ) : (
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                                {documents.map((d) => <DocCard key={d.id} d={d} />)}
                            </div>
                        )}
                    </section>

                    {/* Modules */}
                    {!hideModulesForSeller && (
                        <section>
                            <SectionHeader title="Módulos" />
                            <div className="flex gap-3 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                {MODULES.map(({ to, icon: Icon, label, color }) => (
                                    <Link
                                        key={to}
                                        to={to}
                                        className="flex w-28 shrink-0 flex-col items-center gap-2.5 rounded-xl border border-gray-100 bg-white p-3.5 text-center shadow-sm transition hover:border-primary hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900"
                                    >
                                        <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${color}`}>
                                            <Icon className="h-5 w-5" />
                                        </span>
                                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{label}</span>
                                    </Link>
                                ))}
                                {isSuperAdmin() && (
                                    <Link
                                        to="/settings/intranet"
                                        className="flex w-28 shrink-0 flex-col items-center gap-2.5 rounded-xl border border-gray-100 bg-white p-3.5 text-center shadow-sm transition hover:border-primary hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900"
                                    >
                                        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-gray-500 dark:bg-zinc-800 dark:text-zinc-400">
                                            <PiGearDuotone className="h-5 w-5" />
                                        </span>
                                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Intranet</span>
                                    </Link>
                                )}
                            </div>
                        </section>
                    )}
                </div>

                {/* Events sidebar */}
                <section>
                    <SectionHeader title="Próximos eventos" href="/company/events" />
                    <div className="rounded-xl border border-gray-100 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
                        {loadingEvt ? (
                            <div className="space-y-3 p-4">
                                {[1, 2, 3, 4].map((i) => (
                                    <div key={i} className="flex gap-3">
                                        <Skeleton className="h-12 w-10 shrink-0 rounded-lg" />
                                        <div className="flex-1 space-y-2 pt-1">
                                            <Skeleton className="h-4 w-full" />
                                            <Skeleton className="h-3 w-2/3" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : events.length === 0 ? (
                            <p className="p-6 text-center text-sm text-gray-400">Sem eventos próximos.</p>
                        ) : (
                            <div className="divide-y divide-gray-100 px-4 dark:divide-zinc-800">
                                {events.slice(0, 8).map((e) => <EventRow key={e.id} e={e} />)}
                            </div>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
}
